import { useMemo, useEffect, useCallback, useReducer, useRef } from 'react'
import { useParams } from 'react-router-dom'

import * as analyticsLib from 'global/lib/analytics/analyticsService'
import { formatDate } from 'global/lib/datetime'
import { useFormatMessage } from 'global/lib/localization'
import { reset as resetDataTables } from 'global/redux/features/dataTables/dataTablesSlice'
import { setError, resetError } from 'global/redux/features/report/reportSlice'
import {
  getScanStats,
  getScanThreatLog,
  reset as resetScan,
  resetScanThreatLog
} from 'global/redux/features/scan/scanSlice'
import { isPending } from 'global/redux/toolkit/api'
import productLib from 'global/lib/product/product'

import styles, { NAV_BAR_HEIGHT } from 'global/components/lib/layout/layoutStyles'
import { TabsProps } from 'global/components/lib/Tabs/Tabs'
import { Account } from 'global/types/api/accountType'
import useUserDataLib from 'global/lib/userData/useUserData'

import { getEmployeesReport, resetEmployeesReport } from 'ets/redux/features/reports/employees/employeesSlice'
import { getThreatsReport, resetThreatsReport } from 'ets/redux/features/reports/threats/threatsSlice'
import { getDomainsReport, reset as resetDomainsReport } from 'ets/redux/features/reports/domains/domainsSlice'
import {
  getAttacksByDomainReport,
  resetAttacksByDomainReport
} from 'ets/redux/features/reports/attacksByDomain/attacksByDomainSlice'
import { getChartsReports, resetChartReport } from 'ets/redux/features/reports/chartReport/chartReportSlice'
import { reset as resetReports } from 'ets/redux/features/reports/reportsSlice'
import { useAppDispatch, useAppSelector } from 'ets/redux/toolkit/hooks'
import { IsUserInputDisabledForTable } from 'ets/components/pages/dashboard/isUserInputDisabledForTableType'
import { rerunCurrentScan } from 'ets/redux/features/settings/settingsSlice'

import routesConfig from 'ets/lib/routes/routesConfig'

export interface DashboardLogic {
  error?: string | false
  hasSenLicense: boolean
  isFreeTrialButtonHidden: boolean
  isFromSharedLink: boolean
  inProgress: boolean
  isRerunScanInProgress: boolean
  isUserInputDisabledForTable: IsUserInputDisabledForTable
  isNewScan: boolean
  scanCount: number
  tabsConfig: TabsProps
  fixTableHeight: (tableId: string, newHeight: number) => void
  getSectionElement: (section: string) => HTMLElement | null
  onNewScan: () => void
  scrollToSection: (sectionIndex: number) => void
}

export const TABS = {
  OVERVIEW: 'overview',
  EMPLOYEES: 'employees',
  THREATS: 'threats',
  DOMAINS: 'domains',
  SOURCES_OF_ATTACKS: 'attacks'
}

export const APP_HEADER_HEIGHT = NAV_BAR_HEIGHT
const BASE_I18N_KEY = 'ets.app.dashboard.new_scan'
const BASE_I18N_ERROR_KEY = 'ets.app.error'

export default function useDashboardLogic(): [DashboardLogic] {
  const isScanStartedToLoad = useRef(false)
  const dispatch = useAppDispatch()
  const classes = styles()
  const [userDataLib] = useUserDataLib()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const { reportId } = useParams()
  const {
    accessToken,
    bccAccount,
    error,
    isInitialScanStatsLoaded,
    isReportsInProgress,
    isRerunScanInProgress,
    isScanInProgress,
    isShareSecretSet,
    isStatsInProgress,
    userEmail
  } = useAppSelector(_stores => ({
    accessToken: _stores.accessToken.accessToken,
    bccAccount: _stores.accessToken.bccAccount || '',
    error: _stores.report.error,
    isInitialScanStatsLoaded: _stores.scan.isInitialScanStatsLoaded,
    isReportsInProgress: isPending(_stores.reports.employees.apiStatus),
    isRerunScanInProgress: isPending(_stores.settings.rerunCurrentScanApiStatus),
    isScanInProgress: _stores.scan.stats?.id ? _stores.scan.stats.inProgress : true,
    isShareSecretSet: !!_stores.accessToken.shareSecret?.value,
    isStatsInProgress: !!_stores.scan.stats?.inProgress,
    userEmail: _stores.user.data.email || ''
  }))

  const [state, setState] = useReducer((_state: any, newState: any) => ({ ..._state, ...newState }), {
    activeTab: reportId,
    createNewScan: false,
    scanCount: 0
  })

  /*
   *  All useEffect hooks are at the bottom of the file
   */

  const recentEtsScanATs = useMemo(() => {
    // Retrieve a list of the 5 most recent scans (represented by access token object)
    return userDataLib.getEtsAccessTokensByTenantId(accessToken?.tenantId || '').slice(0, 5)
  }, [accessToken?.tenantId, userDataLib])

  const tabsConfig: TabsProps = useMemo(() => {
    // Create tab props for each recent scan
    const etsScanTabs = recentEtsScanATs.map(token => {
      return {
        id: token?.id,
        label: formatDate(token?.created),
        onClick: token
          ? () => {
              dispatch(resetScan())
              dispatch(resetReports())
              dispatch(resetDataTables())
              dispatch(resetScanThreatLog())
              setState({ activeTab: token?.id, createNewScan: false })
              routesConfig.DASHBOARD.goto({ reportId: token?.id })
            }
          : undefined
      }
    })

    // If there are less than 5 scans, add the `New Scan` tab
    // This is a hacky pattern since we don't have an access token we can't use `goto()` so the access token set in store is going to be the last access token that was navigated to
    if (etsScanTabs.length < 5) {
      etsScanTabs.push({
        id: 'newScan',
        label: `+ ${formatMessage('new_scan_tab')}`,
        onClick: () => {
          setState({ createNewScan: true, activeTab: 'newScan' })
          dispatch(resetScan())
          routesConfig.DASHBOARD.softGoto({ reportId: 'newScan' })
        }
      })
    }

    return {
      activeTab: state.activeTab,
      tabs: etsScanTabs
    } as TabsProps
  }, [dispatch, formatMessage, recentEtsScanATs, state.activeTab])

  const getSectionElement = useCallback((section: string): HTMLElement | null => {
    return document.getElementsByClassName(`${section}-section`)[0] as HTMLElement | null
  }, [])

  const fixTableHeight = useCallback((tableId: string, newHeight: number) => {
    const table = document.getElementsByClassName(`${tableId}-table`)[0] as HTMLElement | null

    if (table) {
      table.style.height = `${newHeight}px`
    }
  }, [])

  const scrollToSection = useCallback(
    (sectionIndex: number) => {
      const sectionElement = getSectionElement(Object.values(TABS)[sectionIndex])
      // Get the scrollable parent element
      const mainElement = document.querySelector(`.${classes.content}`)

      if (sectionElement) {
        if ('scrollBehavior' in document.documentElement.style && mainElement) {
          mainElement.scroll({
            top: sectionElement.offsetTop - APP_HEADER_HEIGHT,
            behavior: 'smooth'
          })
        } else {
          window.scroll(0, sectionElement.offsetTop)
        }
      }
    },
    [classes, getSectionElement]
  )

  const isFreeTrialButtonHidden = useMemo(() => {
    return (
      isShareSecretSet ||
      userDataLib.isMspManagedAccount(accessToken?.id) ||
      (!!productLib.hasSentinelProduct(accessToken?.id || '') &&
        !!productLib.hasForensicsProduct(accessToken?.id || ''))
    )
  }, [isShareSecretSet, userDataLib, accessToken?.id])

  const onNewScan = useCallback(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.NEW_SCAN, { accessToken: accessToken?.id })
    dispatch(resetError())

    // RerunCurrentScan makes a copy of the scan settings and creates a new one, so we should be using the most recent scan
    const accessTokenToCopy = recentEtsScanATs[recentEtsScanATs.length - 1]?.id

    if (accessTokenToCopy) {
      const { accountId } = userDataLib.getAccountByAccessToken(accessTokenToCopy) || ({} as Account)
      dispatch(rerunCurrentScan({ accessTokenId: accessTokenToCopy, accountId, email: userEmail }))
    }

    dispatch(setError(`${BASE_I18N_ERROR_KEY}.unable_to_load_scan`))
  }, [accessToken, dispatch, recentEtsScanATs, userDataLib, userEmail])

  const inProgress = useMemo(() => {
    return !isInitialScanStatsLoaded && !state.createNewScan
  }, [isInitialScanStatsLoaded, state.createNewScan])

  const isUserInputDisabledForTable: IsUserInputDisabledForTable = useCallback(
    (tableData, reportData, isReportLoaded) => {
      const { totalCount } = reportData.report
      const isDataFiltered = !!tableData.search.length || !!tableData.filter

      return !isReportLoaded || (isReportLoaded && !totalCount && !isDataFiltered)
    },
    []
  )

  const isFromSharedLink = useMemo(() => {
    if (window.location.toString().includes('shared')) {
      return true
    }

    return false
  }, [])

  const hasSenLicense = useMemo(() => {
    const account = userDataLib.getAccountByBccId(bccAccount)
    const senSerialState = account?.bccSerialNumbers.sentinel.state
    return senSerialState?.toUpperCase() === 'A'
  }, [bccAccount, userDataLib])

  // init
  useEffect(() => {
    return () => {
      dispatch(resetChartReport())
      dispatch(resetDomainsReport())
      dispatch(resetThreatsReport())
      dispatch(resetEmployeesReport())
      dispatch(resetAttacksByDomainReport())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check query params only on init
  useEffect(() => {
    if (window.location.toString().includes(analyticsLib.QUERY_PARAMS.PDF)) {
      analyticsLib.trackAppEvent(analyticsLib.EVENTS.REPORT_OPENED_IN_PDF, {
        url: window.location.href
      })
    }
  }, [])

  useEffect(() => {
    // user is logged in but the report is being opened from email
    if (window.location.toString().includes('emailscancomplete')) {
      analyticsLib.trackAppEvent(analyticsLib.EVENTS.REPORT_OPENED_IN_EMAIL, {
        url: window.location.href
      })
      routesConfig.DASHBOARD.goto({ reportId })
    }

    if (!isScanStartedToLoad.current) {
      isScanStartedToLoad.current = true
      dispatch(getScanStats({ accessTokenId: reportId }))
      dispatch(getScanThreatLog({ accessTokenId: reportId }))
    }

    if (isStatsInProgress)
      analyticsLib.trackAppEvent(analyticsLib.EVENTS.VIEW_SCAN_IN_PROGRESS, { url: window.location.href })

    if (isInitialScanStatsLoaded && !isScanInProgress && !isReportsInProgress) {
      dispatch(getChartsReports({ accessTokenId: reportId }))
      dispatch(getEmployeesReport({ accessTokenId: reportId }))
      dispatch(getThreatsReport({ accessTokenId: reportId }))
      dispatch(getDomainsReport({ accessTokenId: reportId }))
      dispatch(getAttacksByDomainReport({ accessTokenId: reportId }))

      analyticsLib.trackAppEvent(analyticsLib.EVENTS.DASHBOARD_VIEW, { accessTokenId: reportId })
    }

    // This is needed so that when we click on the tab of the currently viewed scan it refetches the data
    isScanStartedToLoad.current = false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isInitialScanStatsLoaded, isScanInProgress, isStatsInProgress, reportId])

  useEffect(() => {
    setState({ scanCount: 5 - recentEtsScanATs.length })
  }, [recentEtsScanATs.length])

  return useMemo(
    () => [
      {
        error,
        getSectionElement,
        fixTableHeight,
        hasSenLicense,
        isFreeTrialButtonHidden,
        isFromSharedLink,
        inProgress,
        isNewScan: state.createNewScan,
        isRerunScanInProgress,
        isUserInputDisabledForTable,
        onNewScan,
        scanCount: state.scanCount,
        scrollToSection,
        tabsConfig
      }
    ],
    [
      error,
      getSectionElement,
      fixTableHeight,
      hasSenLicense,
      isFreeTrialButtonHidden,
      isFromSharedLink,
      inProgress,
      state.createNewScan,
      state.scanCount,
      isRerunScanInProgress,
      isUserInputDisabledForTable,
      onNewScan,
      scrollToSection,
      tabsConfig
    ]
  )
}
